import { useEffect, useState } from 'react';
import { fetchPutState } from '@iwoca/lapi-client/edge';
import { GetApiIntroducerPortalDirectorSearchResponse } from '@iwoca/lapi-client/introducer_portal';
import { useMutation } from '@tanstack/react-query';
import {
  buildBasePerson,
  normaliseState,
} from 'components/Signup/helpers/stateNormalisation';
import {
  TGetStateResponseData,
  TPutStateRequestBody,
} from 'lendingApi/lapiOverrides.types';
import {
  useGetDirectorSearch,
  useGetIntroducerApplication,
  useGetSignupState,
} from 'lendingApi/lendingApiHooks';
import { trimAllStrings } from 'utils/common/common';
import { TSignupFormData } from '../SignupForm/SignupForm.types';

export const useCheckDetails = () => {
  const [matchingDirector, setMatchingDirector] =
    useState<GetApiIntroducerPortalDirectorSearchResponse['data'][number]>();
  const [directorInitialized, setDirectorInitialized] = useState(false);

  const { application, isLoading: isLoadingIntroducerApplication } =
    useGetIntroducerApplication();
  const { state, refetchState } = useGetSignupState();
  const normalisedState = normaliseState(state);

  const { data: directorsData } = useGetDirectorSearch(
    normalisedState.companyNumber,
  );
  const stateKey = application?.customer?.state_key;

  const { mutateAsync: mutateState } = useMutation({
    mutationFn: async (state: TPutStateRequestBody) => {
      if (!stateKey) return;
      await fetchPutState({ stateKey, body: state });
      return await refetchState();
    },
  });

  useEffect(() => {
    if (!directorsData) {
      return;
    }

    const findMatchingDirector = () => {
      if (!normalisedState?.applicant) {
        return;
      }

      return directorsData.data.find(
        (director) =>
          director.first_name === normalisedState.applicant.firstName &&
          director.last_name === normalisedState.applicant.lastName,
      );
    };

    if (directorsData.data.length > 0) {
      setMatchingDirector(findMatchingDirector());
    }
    setDirectorInitialized(true);
  }, [directorsData, normalisedState?.applicant]);

  const applicantDOBMatchesCompaniesHouse = () => {
    const applicant = normalisedState?.applicant;

    return (
      matchingDirector?.month_of_birth === Number(applicant?.dobMonth) &&
      matchingDirector?.year_of_birth === Number(applicant?.dobYear)
    );
  };

  const denormaliseStateWithOnePerson = (
    prevState: TGetStateResponseData,
    formData: TSignupFormData,
  ): TGetStateResponseData => {
    const applicant = buildBasePerson(
      formData.applicant,
      prevState.application?.people?.[0],
    );

    return {
      ...prevState,
      application: {
        ...prevState.application,
        company: {
          ...prevState.application?.company,
        },
        people: [applicant],
      },
    };
  };

  const updateStateData = async (data: TSignupFormData) => {
    if (!stateKey || !state) {
      throw new Error('Error updating signup data');
    }

    data = trimAllStrings(data);

    const newState = denormaliseStateWithOnePerson(state, data)!;
    await mutateState({ data: newState });
    return;
  };

  return {
    isLoading:
      !state ||
      !application ||
      !directorInitialized ||
      isLoadingIntroducerApplication,
    normalisedState,
    updateStateData,
    applicantDOBMatchesCompaniesHouse,
  };
};
